import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";
import { getApiBaseUrl } from "@/utils/apiUtils";
import { getToken } from "@/utils/authToken";

// Configuração base
const axiosConfig: AxiosRequestConfig = {
  baseURL: getApiBaseUrl(),
  timeout: 60000,
  withCredentials: false,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
};

const apiClient: AxiosInstance = axios.create(axiosConfig);

apiClient.interceptors.request.use(
  (config) => {
    // Token-based auth: inject Authorization: token api_key:api_secret.
    const token = getToken();
    if (token && config.headers) {
      config.headers["Authorization"] = `token ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("❌ Request Error:", error);
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    if (error.response) {
      const { status, data } = error.response;
      console.error(
        `❌ ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
        { status, data },
      );

      if (status === 401) {
        // Lazy import to avoid circular dep between axios helper and authStore.
        try {
          const { useAuthStore } = await import("@/stores/authStore");
          await useAuthStore.getState().logout();
        } catch (e) {
          console.warn("[AXIOS] logout failed during 401:", e);
        }
        window.location.href = "/auth/login";
      }
    } else if (error.request) {
      console.error("Erro de rede - Verifique sua conexão");
    } else {
      console.error("Erro na configuração da request:", error.message);
    }
    return Promise.reject(error);
  },
);

interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success?: boolean;
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
  };
}

// Login client (token vem no corpo da resposta, sem cookie).
const loginClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 60000,
  withCredentials: false,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Public client (no cookies — forgot/reset password etc.).
const publicClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 60000,
  withCredentials: false,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

export const get = async <T = any>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  const response = await apiClient.get(url, config);
  return response.data;
};

export const post = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  const response = await apiClient.post(url, data, config);
  return response.data;
};

export const postLogin = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  const response = await loginClient.post(url, data, config);
  return response.data;
};

export const postPublic = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  const response = await publicClient.post(url, data, config);
  return response.data;
};

export const put = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  const response = await apiClient.put(url, data, config);
  return response.data;
};

export const patch = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  const response = await apiClient.patch(url, data, config);
  return response.data;
};

export const del = async <T = any>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  const response = await apiClient.delete(url, config);
  return response.data;
};

export const upload = async <T = any>(
  url: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<ApiResponse<T>> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        onProgress(progress);
      }
    },
  });

  return response.data;
};

export const download = async (
  url: string,
  filename?: string,
): Promise<void> => {
  const response = await apiClient.get(url, { responseType: "blob" });
  const blob = new Blob([response.data]);
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename || "download";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

// `api` alias kept for legacy code that imports `{ api }` from this module.
export const api = apiClient;
export { apiClient };
export default apiClient;
